import { useLayoutEffect, useRef, useState } from 'react';

const CHIP_MIN_WIDTH_RATIO = 0.8;

type ChipsLayout = {
  visibleCount: number;
  minWidths: number[];
};

type CollapsedChipsOptions = {
  minWidthRatio?: number;
  minVisibleCount?: number;
};

export const useCollapsedChips = (
  selectedIds: string[],
  isEnabled: boolean,
  options: CollapsedChipsOptions = {},
) => {
  const { minWidthRatio = CHIP_MIN_WIDTH_RATIO, minVisibleCount = 1 } = options;
  const selectedCount = selectedIds.length;
  const selectionKey = selectedIds.join(',');
  const chipsRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<ChipsLayout>({
    visibleCount: selectedCount,
    minWidths: [],
  });

  useLayoutEffect(() => {
    const container = chipsRef.current;
    const measure = measureRef.current;
    if (!isEnabled || !container || !measure) {
      return;
    }

    const updateLayout = () => {
      const elements = [...measure.children] as HTMLElement[];
      const counterWidth = elements.at(-1)?.offsetWidth ?? 0;
      const minWidths = elements
        .slice(0, -1)
        .map((element) => Math.ceil(element.offsetWidth * minWidthRatio));
      const gap = parseFloat(getComputedStyle(container).columnGap) || 0;
      const availableWidth = container.clientWidth;

      let usedWidth = 0;
      let fittingCount = 0;
      for (const [index, width] of minWidths.entries()) {
        usedWidth += width + (index > 0 ? gap : 0);
        const hasHidden = index < minWidths.length - 1;
        const requiredWidth = usedWidth + (hasHidden ? gap + counterWidth : 0);
        if (requiredWidth > availableWidth) {
          break;
        }
        fittingCount = index + 1;
      }

      setLayout({
        visibleCount: Math.max(minVisibleCount, fittingCount),
        minWidths: fittingCount > 0 ? minWidths : [],
      });
    };

    updateLayout();
    const observer = new ResizeObserver(updateLayout);
    observer.observe(container);
    return () => observer.disconnect();
  }, [selectionKey, isEnabled, minWidthRatio, minVisibleCount]);

  return {
    chipsRef,
    measureRef,
    visibleCount: isEnabled ? layout.visibleCount : selectedCount,
    minWidths: isEnabled ? layout.minWidths : [],
  };
};
